import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { HttpClient, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ConfigurationService } from './shared/services/configuration.service';
import { DatePipe } from '@angular/common';
import { provideKeycloakAngular } from './keycloak/keycloak.config';
import { includeBearerTokenInterceptor } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { errorInterceptor } from './shared/interceptors/error.interceptor';
import { STORAGE_CONFIG } from './shared/services/local-storage.service';
import { STORAGE_KEYS } from './shared/constants/storage-keys';
import { provideMonacoEditor } from 'ngx-monaco-editor-v2';
import { providePrimeNG } from 'primeng/config';
import DATA_UNIT_THEME from '../../public/theme/dataunit-theme';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import {
  LucideArrowLeftRight,
  LucideBadgeCheck,
  LucideBookOpen,
  LucideBuilding,
  LucideChartColumnBig,
  LucideCodeXml,
  LucideCopy,
  LucideDownload,
  LucideExternalLink,
  LucideHash,
  LucideInfo,
  LucideLayoutGrid,
  LucideLock,
  LucideLogOut,
  LucidePlus,
  LucideRefreshCw,
  LucideServer,
  LucideTerminal,
  LucideTrash2,
  LucideUpload,
  LucideUsers,
  LucideWandSparkles,
  provideLucideIcons,
} from '@lucide/angular';

export const provideLocalStorage = () => ({
  provide: STORAGE_CONFIG,
  useValue: { namespace: 'lipo', mode: 'blob', blobKey: 'settings' },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    providePrimeNG({
      theme: { preset: DATA_UNIT_THEME, options: { darkModeSelector: '.p-dark' } },
    }),
    provideLucideIcons(
      LucideBuilding,
      LucideUsers,
      LucideServer,
      LucideBadgeCheck,
      LucideLayoutGrid,
      LucideArrowLeftRight,
      LucideLogOut,
      LucideChartColumnBig,
      LucideBookOpen,
      LucideTrash2,
      LucideExternalLink,
      LucideHash,
      LucideDownload,
      LucideRefreshCw,
      LucideInfo,
      LucideTerminal,
      LucideLock,
      LucideCopy,
      LucideUpload,
      LucideCodeXml,
      LucidePlus,
      LucideWandSparkles,
    ),
    MessageService,
    DialogService,
    ConfirmationService,
    provideRouter(routes),
    provideAppInitializer(() => {
      const http = inject(HttpClient);
      return firstValueFrom(inject(ConfigurationService).loadConfig(http));
    }),
    provideHttpClient(
      withXhr(),
      withInterceptors([includeBearerTokenInterceptor, errorInterceptor]),
    ),
    provideKeycloakAngular({
      url: 'https://login.dataunit.ch/auth',
      realm: JSON.parse(<string>localStorage.getItem('lipo:settings'))?.[STORAGE_KEYS.REALM] ?? '',
      clientId: 'licenseportal',
    }),
    provideMonacoEditor({
      baseUrl:
        typeof globalThis !== 'undefined' && globalThis.location?.origin
          ? `${globalThis.location.origin}/assets/monaco/min/vs`
          : '/assets/monaco/min/vs',
    }),
    provideLocalStorage(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: 'i18n/' }),
      fallbackLang: 'en',
    }),
    DatePipe,
  ],
};
